import type {Page, Result} from 'framework';
import type {ClearRecentLineIngredients} from '../application/use-cases/clear-recent-line-ingredients';
import type {CreateSavedMeal} from '../application/use-cases/create-saved-meal';
import type {DeleteSavedMeal} from '../application/use-cases/delete-saved-meal';
import type {GetSavedMeal} from '../application/use-cases/get-saved-meal';
import type {ListRecentLineIngredients} from '../application/use-cases/list-recent-line-ingredients';
import type {ListSavedMeals} from '../application/use-cases/list-saved-meals';
import type {LookupLineIngredientByBarcode} from '../application/use-cases/lookup-line-ingredient-by-barcode';
import type {SearchLineIngredients} from '../application/use-cases/search-line-ingredients';
import type {TouchSavedMeal} from '../application/use-cases/touch-saved-meal';
import type {UpdateSavedMeal} from '../application/use-cases/update-saved-meal';
import type {
  CreateSavedMealInput,
  ListRecentLineIngredientsInput,
  ListSavedMealsInput,
  LookupLineIngredientByBarcodeInput,
  SearchLineIngredientsInput,
  UpdateSavedMealInput,
} from '../application/ports/saved-meal.repository';
import type {LineIngredientCandidate} from '../domain/line-ingredient-candidate';
import type {SavedMealError} from '../domain/errors';
import type {RecentLineIngredient} from '../domain/recent-line-ingredient';
import type {SavedMeal, SavedMealSummary} from '../domain/saved-meal';
import type {SavedMealId} from '../domain/saved-meal-id.value-object';

export class SavedMealController {
  constructor(
    private readonly listSavedMeals: ListSavedMeals,
    private readonly getSavedMeal: GetSavedMeal,
    private readonly createSavedMeal: CreateSavedMeal,
    private readonly updateSavedMeal: UpdateSavedMeal,
    private readonly deleteSavedMeal: DeleteSavedMeal,
    private readonly touchSavedMeal: TouchSavedMeal,
    private readonly searchLineIngredientsUseCase: SearchLineIngredients,
    private readonly lookupLineIngredientByBarcodeUseCase: LookupLineIngredientByBarcode,
    private readonly listRecentLineIngredientsUseCase: ListRecentLineIngredients,
    private readonly clearRecentLineIngredientsUseCase: ClearRecentLineIngredients,
  ) {}

  list(input: ListSavedMealsInput): Promise<Result<Page<SavedMealSummary>, SavedMealError>> {
    return this.listSavedMeals.execute(input);
  }

  getById(id: SavedMealId): Promise<Result<SavedMeal, SavedMealError>> {
    return this.getSavedMeal.execute(id);
  }

  create(input: CreateSavedMealInput): Promise<Result<SavedMeal, SavedMealError>> {
    return this.createSavedMeal.execute(input);
  }

  update(input: UpdateSavedMealInput): Promise<Result<SavedMeal, SavedMealError>> {
    return this.updateSavedMeal.execute(input);
  }

  delete(id: SavedMealId): Promise<Result<void, SavedMealError>> {
    return this.deleteSavedMeal.execute(id);
  }

  searchLineIngredients(
    input: SearchLineIngredientsInput,
  ): Promise<Result<readonly LineIngredientCandidate[], SavedMealError>> {
    return this.searchLineIngredientsUseCase.execute(input);
  }

  lookupLineIngredientByBarcode(
    input: LookupLineIngredientByBarcodeInput,
  ): Promise<Result<readonly LineIngredientCandidate[], SavedMealError>> {
    return this.lookupLineIngredientByBarcodeUseCase.execute(input);
  }

  touch(id: SavedMealId): Promise<Result<void, SavedMealError>> {
    return this.touchSavedMeal.execute(id);
  }

  listRecentLineIngredients(
    input: ListRecentLineIngredientsInput,
  ): Promise<Result<readonly RecentLineIngredient[], SavedMealError>> {
    return this.listRecentLineIngredientsUseCase.execute(input);
  }

  clearRecentLineIngredients(): Promise<Result<void, SavedMealError>> {
    return this.clearRecentLineIngredientsUseCase.execute();
  }
}
